import { CarData, Dealer as DealerProps } from 'features/car/types'
import { FC } from 'react'
import { useCarDetailsCardConfig } from 'stages/car-details'
import styled, { useTheme } from 'styled-components'
import { Status } from 'ui/card/components/Status'
import { Icon } from 'ui/icon'

import { getProjectName } from '../../../lib/get-project-name'
import { CallTracking } from '../types'

export type Props = {
  location?: string
  dealers: DealerProps[]
  status: CarData['status']
  callTracking?: CallTracking
  locationPhone?: string
  showLocationAndPhone?: boolean
}

export const DealerInfo: FC<Props> = ({
  location,
  status,
  callTracking,
  locationPhone,
  showLocationAndPhone = false,
  dealers
}) => {
  const { scrollToLocator } = useCarDetailsCardConfig()
  const theme = useTheme()

  return (
    <Wrapper>
      {location && showLocationAndPhone && (
        <Dealer>
          <DealerIcon>
            {getProjectName() !== 'generic_v2' && <Icon name="location" size={0.75} />}
            {getProjectName() === 'generic_v2' && <Icon name="telegram2" size={0.65} />}
          </DealerIcon>
          <Location>
            <Text>{getProjectName() === 'avito' ? dealers[0].district : location}</Text>
            {locationPhone && getProjectName() !== 'avito' && (
              <PhoneWrapper
                className={callTracking ? callTracking.phoneWrapperClass : ''}
                href={`tel:${locationPhone}`}
              >
                <Text>{locationPhone}</Text>
              </PhoneWrapper>
            )}
          </Location>
        </Dealer>
      )}

      {status && getProjectName() !== 'avito' && (
        <StatusWrapper>
          <Status color={status.color} text={status.text} />
          {scrollToLocator && (
            <ToLocator href="#mapLocator">
              {getProjectName() !== 'generic_v2' && (
                <IconToLocator>
                  <path
                    d="M12.214 12.0474L7.49992 16.7615L2.78585 12.0474C1.85351 11.1151 1.21858 9.92719 0.961347 8.63398C0.704119 7.34078 0.836146 6.00034 1.34073 4.78217C1.84532 3.564 2.6998 2.52282 3.79613 1.79028C4.89245 1.05774 6.18138 0.666748 7.49992 0.666748C8.81846 0.666748 10.1074 1.05774 11.2037 1.79028C12.3 2.52282 13.1545 3.564 13.6591 4.78217C14.1637 6.00034 14.2957 7.34078 14.0385 8.63398C13.7813 9.92719 13.1463 11.1151 12.214 12.0474ZM7.49992 10.2963C8.28574 10.2963 9.03938 9.98415 9.59505 9.42848C10.1507 8.87282 10.4629 8.11918 10.4629 7.33335C10.4629 6.54753 10.1507 5.79389 9.59505 5.23822C9.03938 4.68256 8.28574 4.37039 7.49992 4.37039C6.71409 4.37039 5.96045 4.68256 5.40479 5.23822C4.84912 5.79389 4.53696 6.54753 4.53696 7.33335C4.53696 8.11918 4.84912 8.87282 5.40479 9.42848C5.96045 9.98415 6.71409 10.2963 7.49992 10.2963ZM7.49992 8.81484C7.107 8.81484 6.73018 8.65875 6.45235 8.38092C6.17452 8.10309 6.01844 7.72627 6.01844 7.33335C6.01844 6.94044 6.17452 6.56362 6.45235 6.28579C6.73018 6.00796 7.107 5.85187 7.49992 5.85187C7.89283 5.85187 8.26965 6.00796 8.54748 6.28579C8.82531 6.56362 8.9814 6.94044 8.9814 7.33335C8.9814 7.72627 8.82531 8.10309 8.54748 8.38092C8.26965 8.65875 7.89283 8.81484 7.49992 8.81484Z"
                    fill={theme.palette.brand.primary || undefined}
                  />
                </IconToLocator>
              )}
              {getProjectName() === 'generic_v2' && <Icon name="pin2" size={0.75} color={'#828282'} />}
              <span>Показать на карте</span>
            </ToLocator>
          )}
        </StatusWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing[4]};
`

const Dealer = styled.div`
  display: flex;
  align-items: ${() => getProjectName() === 'generic_v2' && 'flex-start'};
  margin-right: ${({ theme }) => theme.spacing[5]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  width: 100%;
`

const Location = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
`

const DealerIcon = styled.div`
  margin-right: 3px;
  color: ${({ theme }) => theme.palette.grey[500]};

  svg {
    fill: ${() => {
      if (getProjectName() === 'generic_v2') return 'transparent'
      return
    }};
  }
`

const Text = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  ${({ theme }) => theme.textStyles.p3};
  color: ${({ theme }) => theme.palette.grey[700]};
`

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.textStyles.p3};
  color: ${({ theme }) => theme.palette.grey[500]};
`

const PhoneWrapper = styled.a`
  text-decoration: none;
`

const ToLocator = styled.a`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.brand.primary};
  margin-left: ${({ theme }) => theme.spacing[3]};
  cursor: pointer;
  text-decoration: none;

  span {
    color: ${({ theme }) => {
      if (getProjectName() === 'ora') return theme.palette.brand.secondary
      if (getProjectName() === 'generic_v2') return theme.palette.grey[600]
      return theme.palette.brand.primary
    }};

    text-decoration: ${() => {
      if (getProjectName() === 'ora') return 'underline'
      return null
    }};

    font-size: ${({ theme }) => {
      if (getProjectName() === 'generic_v2') return '14px'
      return '12px'
    }};
  }

  div {
    margin-right: ${() => {
      if (getProjectName() === 'generic_v2') return '4px'
      return
    }};
  }

  path {
    fill: ${({ theme }) => getProjectName() === 'generic_v2' && theme.palette.grey[600]};
  }
`

const IconToLocator = styled.svg`
  width: ${({ theme }) => theme.spacing[4]};
  height: ${({ theme }) => theme.spacing[4]};
  margin-right: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.spacing[3]};
  display: block;
  flex-shrink: 0;

  path {
    fill: ${({ theme }) => {
      if (getProjectName() === 'ora') return theme.palette.black
      if (getProjectName() === 'generic_v2') return theme.palette.grey[600]
      return theme.palette.brand.primary
    }};
  }
`
